import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Router } from "@angular/router";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { NotifierService } from "src/app/_services/notifier.service";
import { Subscription } from "rxjs";

type DisplayValue =
  | { type: "text"; text: string }
  | { type: "list"; items: string[] }
  | { type: "pairs"; pairs: PairValue[] }
  | { type: "object-list"; items: PairValue[][] };

type PairValue = {
  key: string;
  value: string | DisplayValue;
  isNested: boolean;
};

@Component({
  selector: "app-ec2-request-review",
  templateUrl: "./ec2-request-review.component.html",
  styleUrls: ["../../launch-ec2.operations.component.css"],
})
export class Ec2RequestReviewComponent implements OnInit, OnDestroy {
  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;

  requestId: string = "";
  awsAccountId: string = "";
  region: string = "";
  returnToServiceCatalog: boolean = false;

  loading: boolean = false;
  actionLoading: boolean = false;
  error: string = "";
  actionMessage: string = "";
  actionError: string = "";
  data: any = null;
  showApproveRejectButtons: boolean = true;
  summaryRows: any[] = [];
  requestPayloadRows: any[] = [];
  currentMessage: Subscription;
  private loadSequence: number = 0;
  private destroyed: boolean = false;
  private readonly summaryKeys: string[] = [
    "request_id",
    "requester_id",
    "aws_account_id",
    "region",
    "created_at",
    "status",
  ];
  private readonly payloadPreferredOrder: string[] = [
    "instance_name",
    "platform",
    "architecture",
    "ami_id",
    "instance_type",
    "hourly_cost_usd",
    "monthly_cost_usd",
    "key_name",
    "vpc_id",
    "subnet_id",
    "security_group_ids",
    "associate_public_ip_address",
    "tags",
    "metadata_options",
    "monitoring",
    "placement",
    "ebs_optimized",
    "instance_initiated_shutdown_behavior",
    "iam_instance_profile_arn",
    "user_data",
    "block_device_mappings",
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiServer: APIService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.requestId = (params.get("request_id") || "").trim();
      this.awsAccountId = (this.route.snapshot.queryParamMap.get("aws_account_id") || "").trim();
      this.region = (this.route.snapshot.queryParamMap.get("region") || "").trim();
      this.returnToServiceCatalog =
        (this.route.snapshot.queryParamMap.get("source") || "").trim() === "service-catalog";
      this.loadRequest();
    });

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        const d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId" || d.key == "regionId") {
          this.handleContextChange(d.key, d.value);
        }
      } catch {}
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
    this.notifier.loading(false);
  }

  private headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private normalizeContextValue(value: any): string {
    const v = (value ?? "").toString().trim();
    if (!v || v.toLowerCase() == "null" || v.toLowerCase() == "undefined") {
      return "";
    }
    return v;
  }

  private handleContextChange(key: string, value: any) {
    const nextAccountId =
      key === "accountId"
        ? this.normalizeContextValue(value)
        : this.normalizeContextValue(localStorage.getItem("accountId"));
    const nextRegion =
      key === "regionId"
        ? this.normalizeContextValue(value)
        : this.normalizeContextValue(localStorage.getItem("regionId"));

    if (nextAccountId !== this.awsAccountId || nextRegion !== this.region) {
      this.goBackToDashboard();
    }
  }

  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      if (typeof r == "string") return null;
      return r;
    } catch (err) {
      console.error("API error:", err);
      return null;
    }
  }

  private async triggerAction(action: "approve" | "reject") {
    this.actionMessage = "";
    this.actionError = "";

    if (!this.requestId) {
      this.actionError = "Request ID is missing.";
      return;
    }

    this.actionLoading = true;
    this.notifier.loading(true);
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/launch-ec2?action=${action}&requestId=${encodeURIComponent(
      this.requestId
    )}`;
    const r = await this.get(url);
    this.actionLoading = false;
    this.notifier.loading(false);

    if (!r) {
      this.actionError = `Unable to ${action} request.`;
      return;
    }

    const actionLabel = action === "approve" ? "Approve" : "Reject";

    const message =
      r.message || r.msg || r.detail || `Request ${actionLabel} successfully.`;

    this.actionMessage = String(message);
    this.goBackToDashboard();
  }

  private toLabel(key: string): string {
    const labelOverrides: { [key: string]: string } = {
      request_id: "Request ID",
      requester_id: "Requested By",
      aws_account_id: "AWS Account ID",
      ami_id: "AMI ID",
      vpc_id: "VPC ID",
      subnet_id: "Subnet ID",
      security_group_ids: "Security Group IDs",
      associate_public_ip_address: "Associate Public IP Address",
      iam_instance_profile_arn: "IAM Instance Profile ARN",
      ebs_optimized: "EBS Optimized",
      hourly_cost_usd: "Hourly Cost (USD)",
      monthly_cost_usd: "Monthly Cost (USD)",
      ebs: "EBS",
      key: "Key",
      value: "Value",
    };
    if (labelOverrides[key]) return labelOverrides[key];
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private toDisplayValue(value: any): DisplayValue {
    if (value === null || value === undefined || value === "") {
      return { type: "text", text: "-" };
    }
    if (Array.isArray(value)) {
      const hasObjectItems = value.some(
        (x) => x !== null && x !== undefined && typeof x === "object"
      );
      if (hasObjectItems) {
        return {
          type: "object-list",
          items: value.map((x) => this.objectToPairs(x)),
        };
      }
      return { type: "list", items: value.map((x) => this.stringifyPrimitive(x)) };
    }
    if (typeof value === "object") {
      return { type: "pairs", pairs: this.objectToPairs(value) };
    }
    return { type: "text", text: this.stringifyPrimitive(value) };
  }

  private objectToPairs(value: any): PairValue[] {
    if (!value || typeof value !== "object") {
      return [{ key: "Value", value: this.stringifyPrimitive(value), isNested: false }];
    }
    return Object.keys(value).map((k) => {
      const val = value[k];
      const isNested = Array.isArray(val) || (val && typeof val === "object");
      return {
        key: this.toLabel(k),
        value: isNested ? this.toDisplayValue(val) : this.stringifyPrimitive(val),
        isNested,
      };
    });
  }

  formatTagPair(tagItem: PairValue[]): string {
    const key = tagItem.find((x) => x.key.toLowerCase() === "key")?.value || "-";
    const value = tagItem.find((x) => x.key.toLowerCase() === "value")?.value || "-";
    return `key: ${key} value: ${value}`;
  }

  getTagKey(tagItem: PairValue[]): string {
    return String(
      tagItem.find((x) => x.key.toLowerCase() === "key")?.value || "-"
    );
  }

  getTagValue(tagItem: PairValue[]): string {
    return String(
      tagItem.find((x) => x.key.toLowerCase() === "value")?.value || "-"
    );
  }

  private stringifyPrimitive(value: any): string {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  }

  private formatDateToIst(value: any): string {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }) + " IST";
  }

  private buildRows() {
    this.summaryRows = this.summaryKeys.map((key) => ({
      label: this.toLabel(key),
      display:
        key === "created_at"
          ? { type: "text", text: this.formatDateToIst(this.data?.[key]) as string }
          : this.toDisplayValue(this.data?.[key]),
    }));

    const payload = this.data?.request_payload || {};
    const payloadExcludedKeys = ["aws_account_id", "region", "min_count", "max_count"];
    const payloadKeys = [
      ...this.payloadPreferredOrder,
      ...Object.keys(payload).filter(
        (k) =>
          !this.payloadPreferredOrder.includes(k) &&
          !payloadExcludedKeys.includes(k)
      ),
    ];

    this.requestPayloadRows = payloadKeys
      .filter((key) => !payloadExcludedKeys.includes(key))
      .map((key) => ({
      label: this.toLabel(key),
      display: this.toDisplayValue(
        payload[key] !== undefined ? payload[key] : this.data?.[key]
      ),
      }));
  }

  async loadRequest() {
    const sequence = ++this.loadSequence;
    const requestId = this.requestId;
    const awsAccountId = this.awsAccountId;
    const region = this.region;

    this.data = null;
    this.error = "";

    if (!requestId) {
      this.error = "Invalid request id.";
      return;
    }
    if (!awsAccountId || !region) {
      this.error = "aws_account_id and region are required in query params.";
      return;
    }

    this.loading = true;
    this.notifier.loading(true);
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/create_ec2_instance/api/ec2/request/${encodeURIComponent(
      requestId
    )}?aws_account_id=${encodeURIComponent(awsAccountId)}&region=${encodeURIComponent(
      region
    )}`;
    const r = await this.get(url);
    if (
      this.destroyed ||
      sequence !== this.loadSequence ||
      requestId !== this.requestId ||
      awsAccountId !== this.awsAccountId ||
      region !== this.region
    ) {
      return;
    }

    this.loading = false;
    this.notifier.loading(false);

    if (!r) {
      this.error = "Unable to fetch request details.";
      return;
    }
    this.data = r;
    this.showApproveRejectButtons =
      this.data?.disable_approve_reject_btn !== true;
    this.buildRows();
  }

  onAccept() {
    this.triggerAction("approve");
  }

  onReject() {
    this.triggerAction("reject");
  }

  goBackToDashboard() {
    if (this.returnToServiceCatalog) {
      this.router.navigate(["/dash/operations/service-catalog"], {
        queryParams: { service: "ec2" },
      });
      return;
    }
    this.router.navigate(["/dash/operations/ec2-dashboard"]);
  }
}
